import type { CardServiceFeeQuote } from '@/hooks/payments/useCardServiceFeeQuote';

export interface ChargeTotals {
  /** The base after the tenant's ACH/check discount; the base itself on a card. */
  discountedBase: number;
  /** What the discount took off the base; 0 when none applies. */
  discountAmount: number;
  /** The card service fee the server quoted, added on top. */
  feeAmount: number;
  /** What leaves the account before any fee: discounted base plus the tip. */
  debitAmount: number;
  /** Everything the customer is charged. */
  chargeTotal: number;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * What a staff-started charge actually takes, from the server's quote.
 *
 * The server takes the tenant's ACH/check discount off the base only — never
 * off the tip — and quotes the discounted base as `chargeCents`; a card under
 * the same programme is quoted with nothing off. The fee rides on top of both.
 *
 * Shared because two surfaces state these figures to an operator before the
 * charge goes out — the new-method collector and the job's Record payment
 * dialog — and a disclosure that disagrees with what is debited is worse than
 * none. Computing it twice is how they drift.
 */
export function resolveChargeTotals(
  quote: CardServiceFeeQuote | null | undefined,
  baseAmount: number,
  tipAmount = 0,
): ChargeTotals {
  const base = Number.isFinite(baseAmount) && baseAmount > 0 ? baseAmount : 0;
  const tip = Number.isFinite(tipAmount) && tipAmount > 0 ? tipAmount : 0;
  const feeAmount = (quote?.feeCents ?? 0) / 100;
  const discountedBase =
    quote?.achCheckDiscountEnabled && typeof quote.chargeCents === 'number'
      ? quote.chargeCents / 100
      : base;
  const discountAmount = Math.max(0, round2(base - discountedBase));
  const debitAmount = round2(discountedBase + tip);
  return {
    discountedBase,
    discountAmount,
    feeAmount,
    debitAmount,
    chargeTotal: round2(debitAmount + feeAmount),
  };
}
